import { useCallback, useRef } from 'react';
import { ToolCallMessage, ToolResultMessage } from '../types';
import { getToolRegistry } from '../tools';

interface UseToolExecutionOptions {
  onToolStart?: (toolCall: ToolCallMessage) => void;
  onToolComplete?: (result: ToolResultMessage) => void;
  onToolError?: (toolCall: ToolCallMessage, error: string) => void;
}

interface UseToolExecutionReturn {
  executeTool: (toolCall: ToolCallMessage) => Promise<ToolResultMessage>;
  executeTools: (toolCalls: ToolCallMessage[]) => Promise<ToolResultMessage[]>;
  isExecuting: boolean;
}

export const useToolExecution = (options: UseToolExecutionOptions = {}): UseToolExecutionReturn => {
  const { onToolStart, onToolComplete, onToolError } = options;
  const isExecutingRef = useRef(false);

  const executeTool = useCallback(
    async (toolCall: ToolCallMessage): Promise<ToolResultMessage> => {
      const registry = getToolRegistry();

      onToolStart?.(toolCall);

      try {
        const result = await registry.execute(toolCall.name, toolCall.arguments);

        const toolResult: ToolResultMessage = {
          toolCallId: toolCall.id,
          name: toolCall.name,
          result: result.success ? result.data : { error: result.error },
          isError: !result.success,
        };

        onToolComplete?.(toolResult);
        return toolResult;
      } catch (error) {
        const errorMessage = (error as Error).message;
        onToolError?.(toolCall, errorMessage);

        return {
          toolCallId: toolCall.id,
          name: toolCall.name,
          result: { error: errorMessage },
          isError: true,
        };
      }
    },
    [onToolStart, onToolComplete, onToolError],
  );

  const executeTools = useCallback(
    async (toolCalls: ToolCallMessage[]): Promise<ToolResultMessage[]> => {
      isExecutingRef.current = true;

      try {
        // Execute tools sequentially to avoid race conditions with data fetching
        const results: ToolResultMessage[] = [];
        for (const toolCall of toolCalls) {
          const result = await executeTool(toolCall); // eslint-disable-line no-await-in-loop
          results.push(result);
        }
        return results;
      } finally {
        isExecutingRef.current = false;
      }
    },
    [executeTool],
  );

  return {
    executeTool,
    executeTools,
    isExecuting: isExecutingRef.current,
  };
};
